import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  IconButton,
  Typography,
  CircularProgress,
  Checkbox,
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  Chip,
  Tooltip,
  CardActionArea,
  Card,
  MenuItem,
} from "@mui/material";

import Grid from "@mui/material/Unstable_Grid2";

import { useReactToPrint } from "react-to-print";

import { FormsAPI } from "@services/API";

import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import moment from "moment";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpIcon from "@mui/icons-material/Help";
import AssessmentIcon from "@mui/icons-material/Assessment";
import Logo from "@assets/images/logo_3.png";

import { apiGet } from "@services/API";
import { useSnackbar } from "notistack";
import GaugeChart from "react-gauge-chart";

import HospitalAddEditDialog from "../components/HospitalAddEditDialog";

function headers() {
  let headers = {};
  let token = localStorage.getItem("covid19_token");
  let mfa_token = localStorage.getItem("mfa_token");
  if (token && token.length > 0 && mfa_token && mfa_token.length > 0) {
    headers = { "x-access-token": token };
  }
  return headers;
}

export default function Hospitals({ ...props }) {
  let history = useHistory();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(moment().format("YYYY-MM-DD"));
  const [data, setData] = useState([]);
  const [dataSelectedHospitals, setDataSelectedHospitals] = useState([]);
  const [dataSummaryTotals, setDataSummaryTotals] = useState({
    usedBeds: 0,
    totalBeds: 0,
    wardCount: 0,
    patientCount: 0,
    consultantCount: 0,
    nurseCount: 0,
  });

  const [hospitalDetailsRows, setHospitalDetailsRows] = useState(null);
  const [isHospitalAddEditVisible, setIsHospitalAddEditVisible] =
    useState(false);

  const [selectedHospitalID, setSelectedHospitalID] = useState(null);

  const [isPrintMode, setIsPrintMode] = useState(false);

  const componentRef = useRef();
  const promiseResolveRef = useRef(null);

  useEffect(() => {
    if (isPrintMode && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrintMode]);

  useEffect(() => {
    let level = parseInt(localStorage.getItem("user_level"), 10);

    if (!isNullOrEmpty(level)) {
      if (level === 1 || level === 2 || level === 3) {
        fetchData();
      } else {
        history.push("/wards");
      }
    } else {
      history.push("/login");
    }
  }, []);

  const fetchData = () => {
    const timeOut = setTimeout(() => {
      setLoading(true);

      apiGet(`/hospitals`, { headers: headers() }, history)
        .then((response) => {
          setLoading(false);
          setData(response.data);
          const allHospitalIds = response.data.map((item) => item.id);
          setDataSelectedHospitals(allHospitalIds);
        })
        .catch((error) => {
          setLoading(false);
          setData([]);
          setDataSelectedHospitals([]);
        });
    }, 200);

    return () => clearTimeout(timeOut);
  };

  useEffect(() => {
    const totals = calculateTotalsForHospitals(dataSelectedHospitals);
    setDataSummaryTotals(totals);
  }, [dataSelectedHospitals]);

  const showSnackBar = (type, title, subtitle) => {
    const options = { variant: type };

    enqueueSnackbar(
      <div style={{ display: "inline-grid" }}>
        <Typography data-test={"snack-title"} variant="h6">
          {title}
        </Typography>
        <Typography data-test={"snack-text"} variant="subtitle1">
          {subtitle}
        </Typography>
      </div>,
      options
    );
  };

  useEffect(() => {
    getHospitalDetailsRows(dataSelectedHospitals, currentDate);
  }, [currentDate, dataSelectedHospitals]);

  const standardInputHeight = "56px";

  const isNullOrEmpty = (value) => {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === "string" && value.trim().toLowerCase() === "null") {
      return true;
    }

    if (Array.isArray(value) && value.length === 0) {
      return true;
    }

    if (typeof value === "string" && value.trim() === "") {
      return true;
    }

    if (typeof value === "number" && isNaN(value)) {
      return true;
    }

    return false;
  };

  const handleAddEditHospital = (hospitalID) => {
    setSelectedHospitalID(hospitalID);
    setIsHospitalAddEditVisible(true);
  };

  const handelCloseHospital = () => {
    setSelectedHospitalID(null);
    setIsHospitalAddEditVisible(false);
  };

  const handelSaveHospital = () => {
    setSelectedHospitalID(null);
    setIsHospitalAddEditVisible(false);
    fetchData();
  };

  const handleItemClick = (item) => {
    localStorage.setItem("hospital_id", item.id);
    history.push("/wards");
  };

  const generateOccupancyGraph = (usedBeds, totalBeds) => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          width: 90,
          height: 90,
        }}
      >
        <Typography variant="subtitle2">Occupancy</Typography>
        <GaugeChart
          //id={`gauge-${element.title}`}
          nrOfLevels={3}
          arcsLength={[0.3, 0.5, 0.2]}
          colors={["#00c9a7", "#ffc473", "#ff5f6d"]}
          percent={totalBeds > 0 ? (usedBeds / totalBeds).toFixed(1) : 0.0}
          hideText={true}
        />
        <Typography variant="caption">
          {usedBeds} of {totalBeds} Beds
        </Typography>
      </div>
    );
  };

  const handleHospitalSelect = (event) => {
    console.log("handleHospitalSelect change :", event.target.value);
    const selectedValues = event.target.value;
    setDataSelectedHospitals(selectedValues);
  };

  const calculateTotalsForHospitals = (selectedValues) => {
    const totals = {
      usedBeds: 0,
      totalBeds: 0,
      wardCount: 0,
      patientCount: 0,
      consultantCount: 0,
      nurseCount: 0,
    };

    for (const hospital of data) {
      if (selectedValues.includes(hospital.id)) {
        totals.usedBeds += hospital.used_beds;
        totals.totalBeds += hospital.total_beds;
        totals.wardCount += hospital.ward_count;
        totals.patientCount += hospital.patient_count;
        totals.consultantCount += hospital.doctor_count;
        totals.nurseCount += hospital.nurse_count;
      }
    }

    return totals;
  };

  const getHospitalDetailsRows = (hospital_ids, events_date) => {
    const params = {
      hospital_ids: hospital_ids.join(","),
    };

    FormsAPI.get(
      `/gethospitalsoforigin/${events_date}`,
      { params: params },
      {
        headers: headers(),
      }
    )
      .then((response) => {
        console.log(" getHospitalDetailsRows : response:", response);

        let hospitals = response.data.result;

        const totals = hospitals.reduce(
          (acc, curr) => {
            acc.active_patients += curr.active_patients;
            acc.edd += curr.edd;
            acc.tci += curr.tci;
            acc.expected_today += curr.expected_today;
            return acc;
          },
          { active_patients: 0, edd: 0, tci: 0, expected_today: 0 }
        );

        totals.hospital = "Total";

        hospitals.push(totals);

        setHospitalDetailsRows(hospitals);
      })
      .catch((error) => {
        console.error(" getHospitalDetailsRows : error:", error);
      });
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsPrintMode(true);
      });
    },
    onAfterPrint: () => {
      promiseResolveRef.current = null;
      setIsPrintMode(false);
    },
  });

  return (
    <Box style={{ padding: 15 }}>
      <Grid container spacing={2}>
        {loading ? (
          <Grid item xs={12}>
            <CircularProgress color="primary" />
          </Grid>
        ) : (
          <>
            <Grid
              container
              xs={12}
              md={12}
              lg={12}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "0px 0",
              }}
            >
              <Grid
                item
                xs={6}
                style={{ display: "flex", alignItems: "center" }}
              >
                <Typography
                  data-test={"hospital-header"}
                  variant="h5"
                  style={{ paddingRight: 5 }}
                >
                  Hospitals
                </Typography>
                <Tooltip title="Add Hospital">
                  <IconButton
                    data-test={"hospital-add-hospital-btn"}
                    aria-label="add"
                    size="large"
                    color="primary"
                    onClick={() => handleAddEditHospital(null)}
                  >
                    <AddCircleIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Grid>

              <Grid
                item
                xs={6}
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  flexGrow: 1,
                }}
              >
                <Button
                  data-test={"reports-print-btn"}
                  variant="contained"
                  color="primary"
                  style={{ marginRight: 10 }}
                  disabled={false}
                  onClick={() => {
                    handlePrint();
                  }}
                >
                  Print
                </Button>
              </Grid>
            </Grid>

            {!isNullOrEmpty(data) ? (
              <Grid
                item
                xs={12}
                style={{
                  ...(isPrintMode && { margin: 30 }), // Set your desired margin here
                }}
                ref={componentRef}
              >
                <>
                  {isPrintMode && (
                    <>
                      <Grid item xs={12}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            paddingBottom: 20,
                          }}
                        >
                          <img src={Logo} alt="Logo" style={{ height: 55 }} />
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="h5"
                          style={{ textAlign: "center", fontWeight: "bold" }}
                        >
                          Hospital Dashboard
                        </Typography>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Grid
                      container
                      spacing={1}
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          id="outlined-select-hospital"
                          select
                          fullWidth
                          label="Hospitals"
                          value={dataSelectedHospitals}
                          onChange={handleHospitalSelect}
                          SelectProps={{
                            multiple: true,
                            margin: "dense",
                            padding: 0,
                            renderValue: (selected) => (
                              <div
                                style={{ display: "flex", flexWrap: "wrap" }}
                              >
                                {selected.map((item) => (
                                  <Chip
                                    key={item}
                                    label={
                                      data.find(
                                        (hospital) => hospital.id === item
                                      ).name
                                    }
                                    style={{ margin: 3, padding: 1 }}
                                  />
                                ))}
                              </div>
                            ),
                          }}
                          variant="outlined"
                          size="small"
                        >
                          {data.map((hospital) => (
                            <MenuItem key={hospital.id} value={hospital.id}>
                              <Checkbox
                                checked={
                                  dataSelectedHospitals.indexOf(hospital.id) >
                                  -1
                                }
                              />
                              <ListItemText primary={hospital.name} />
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      {/* <Grid
                        item
                        xs={12}
                        md={6}
                        lg={3}
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          //padding: "0px 0",
                        }}
                      >
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DateTimePicker
                            label="Dashboard Date & Time"
                            slotProps={{
                              textField: {
                                variant: "outlined",
                                //size: "small",
                                fullWidth: true,
                              },
                            }}
                            inputFormat="DD/MM/YYYY HH:mm"
                            value={moment(currentDate, "YYYY-MM-DD HH:mm:ss")}
                            onChange={(date) => {
                              console.log(
                                "Date and Time changed:",
                                moment(date, "YYYY-MM-DD HH:mm:ss")
                              );
                              setCurrentDate(
                                moment(date).format("YYYY-MM-DD HH:mm:ss")
                              );
                            }}
                            onAccept={(date) => {
                              console.log(
                                "Date and Time selected:",
                                moment(date).format("YYYY-MM-DD HH:mm:ss")
                              );
                              setCurrentDate(
                                moment(date).format("YYYY-MM-DD HH:mm:ss")
                              );
                            }}
                            data-test="dashboard-date-time-picker"
                          />
                        </LocalizationProvider>
                      </Grid> */}

                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={4}
                        style={{
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {generateOccupancyGraph(
                          dataSummaryTotals.usedBeds,
                          dataSummaryTotals.totalBeds
                        )}
                      </Grid>

                      <Grid item xs={3} md={1} style={{ textAlign: "center" }}>
                        <Typography variant="subtitle2">Wards</Typography>
                        <Typography>{dataSummaryTotals.wardCount}</Typography>
                      </Grid>

                      {/* <Grid item xs={3} md={1} style={{ textAlign: "center" }}>
                      <Typography variant="subtitle2">Patients</Typography>
                      <Typography>{dataSummaryTotals.patientCount}</Typography>
                    </Grid> */}

                      <Grid item xs={3} md={1} style={{ textAlign: "center" }}>
                        <Typography variant="subtitle2">Consultants</Typography>
                        <Typography>
                          {dataSummaryTotals.consultantCount}
                        </Typography>
                      </Grid>

                      <Grid item xs={3} md={1} style={{ textAlign: "center" }}>
                        <Typography variant="subtitle2">Nurses</Typography>
                        <Typography>{dataSummaryTotals.nurseCount}</Typography>
                      </Grid>

                      {!isPrintMode && (
                        <Grid
                          item
                          xs={12}
                          md={1}
                          style={{ textAlign: "center" }}
                        >
                          <Tooltip title="Help">
                            <IconButton
                              aria-label="help"
                              size="large"
                              color="primary"
                              onClick={(event) => {
                                event.stopPropagation();
                                showSnackBar(
                                  "info",
                                  "Info",
                                  "This feature is not available."
                                );
                              }}
                            >
                              <HelpIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid
                    container
                    xs={12}
                    md={12}
                    lg={12}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0px 0",
                    }}
                  >
                    <Grid item xs={12}>
                      <Typography
                        data-test={"hospital-list-title"}
                        variant="subtitle2"
                        style={{ textAlign: "center" }}
                      >
                        Hospital of Origin
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      md={1}
                      lg={3}
                      style={{
                        display: "flex",
                        justifyContent: "left",
                        alignItems: "left",
                        padding: "0px 0",
                      }}
                    >
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                          label="Hospital of Origin Dashboard Date & Time"
                          slotProps={{
                            textField: {
                              variant: "outlined",
                              //size: "small",
                              fullWidth: true,
                            },
                          }}
                          inputFormat="DD/MM/YYYY HH:mm"
                          timeSteps={{ minutes: 1 }}
                          value={moment(currentDate, "YYYY-MM-DD HH:mm:ss")}
                          onChange={(date) => {
                            console.log(
                              "Date and Time changed:",
                              moment(date, "YYYY-MM-DD HH:mm:ss")
                            );
                            setCurrentDate(
                              moment(date).format("YYYY-MM-DD HH:mm:ss")
                            );
                          }}
                          onAccept={(date) => {
                            console.log(
                              "Date and Time selected:",
                              moment(date).format("YYYY-MM-DD HH:mm:ss")
                            );
                            setCurrentDate(
                              moment(date).format("YYYY-MM-DD HH:mm:ss")
                            );
                          }}
                          data-test="dashboard-date-time-picker"
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <TableContainer
                      component={Paper}
                      style={{ border: "2px solid #ebf5ef" }}
                    >
                      <Table>
                        <TableHead style={{ backgroundColor: "#ebf5ef" }}>
                          <TableRow>
                            <TableCell>Hospital of Origin</TableCell>
                            <TableCell align="right">Active Patients</TableCell>
                            <TableCell align="right">
                              EDD on{" "}
                              {moment(currentDate).format("MM/DD/YYYY hh:mm A")}
                            </TableCell>
                            <TableCell align="right">
                              TCI on{" "}
                              {moment(currentDate).format("MM/DD/YYYY hh:mm A")}
                            </TableCell>
                            <TableCell align="right">
                              Expected at{" "}
                              {moment(currentDate).format("MM/DD/YYYY hh:mm A")}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {hospitalDetailsRows &&
                            hospitalDetailsRows.map((row, index) => (
                              <TableRow key={index}>
                                <TableCell
                                  component="th"
                                  scope="row"
                                  style={{
                                    fontWeight:
                                      row.hospital === "Total"
                                        ? "bold"
                                        : "normal",
                                  }}
                                >
                                  {row.hospital}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  style={{
                                    fontWeight:
                                      row.hospital === "Total"
                                        ? "bold"
                                        : "normal",
                                  }}
                                >
                                  {row.active_patients}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  style={{
                                    fontWeight:
                                      row.hospital === "Total"
                                        ? "bold"
                                        : "normal",
                                  }}
                                >
                                  {row.edd}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  style={{
                                    fontWeight:
                                      row.hospital === "Total"
                                        ? "bold"
                                        : "normal",
                                  }}
                                >
                                  {row.tci}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  style={{
                                    fontWeight:
                                      row.hospital === "Total"
                                        ? "bold"
                                        : "normal",
                                  }}
                                >
                                  {row.expected_today}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>{" "}
                  </Grid>

                  {!isPrintMode && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          data-test={"hospital-list-title"}
                          variant="subtitle2"
                          style={{ textAlign: "center" }}
                        >
                          Hospital List
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      {dataSelectedHospitals.length > 0 ? (
                        <Grid item xs={12}>
                          <List>
                            {data
                              .filter((item) =>
                                dataSelectedHospitals.includes(item.id)
                              )
                              .map((item, index) => (
                                <ListItem
                                  key={item.id}
                                  data-test="hospital-entry"
                                >
                                  <Card
                                    elevation={2}
                                    style={{
                                      width: "100%",
                                      backgroundColor: "#ebf5ef",
                                    }}
                                  >
                                    <CardActionArea
                                      style={{ padding: 10 }}
                                      onClick={() => handleItemClick(item)}
                                    >
                                      <Grid
                                        container
                                        spacing={1}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Grid
                                          item
                                          xs={12}
                                          sm={6}
                                          md={2}
                                          style={{ textAlign: "center" }}
                                        >
                                          <Typography
                                            data-test="hospital-name"
                                            variant="subtitle1"
                                            fontWeight={"bold"}
                                          >
                                            {item.name}
                                          </Typography>
                                          <Typography
                                            data-test="hospital-location"
                                            variant="caption"
                                          >
                                            {item.location}
                                          </Typography>
                                        </Grid>

                                        <Grid
                                          item
                                          xs={6}
                                          sm={6}
                                          md={1}
                                          style={{
                                            display: "flex",
                                            justifyContent: "center",
                                          }}
                                        >
                                          {generateOccupancyGraph(
                                            item.used_beds,
                                            item.total_beds
                                          )}
                                        </Grid>

                                        <Grid
                                          item
                                          xs={6}
                                          sm={6}
                                          md={2}
                                          style={{ textAlign: "center" }}
                                        >
                                          <Typography variant="subtitle2">
                                            Data Controller
                                          </Typography>
                                          <Typography data-test="hospital-data-controller">
                                            {item.data_controller}
                                          </Typography>
                                        </Grid>

                                        <Grid
                                          item
                                          xs={3}
                                          sm={6}
                                          md={1}
                                          style={{ textAlign: "center" }}
                                        >
                                          <Typography variant="subtitle2">
                                            Wards
                                          </Typography>
                                          <Typography>
                                            {item.ward_count}
                                          </Typography>
                                        </Grid>

                                        <Grid
                                          item
                                          xs={3}
                                          sm={6}
                                          md={1}
                                          style={{ textAlign: "center" }}
                                        >
                                          <Typography variant="subtitle2">
                                            Active Patients
                                          </Typography>
                                          <Typography>
                                            {item.patient_count}
                                          </Typography>
                                        </Grid>

                                        <Grid
                                          item
                                          xs={3}
                                          sm={6}
                                          md={1}
                                          style={{ textAlign: "center" }}
                                        >
                                          <Typography variant="subtitle2">
                                            Consultants
                                          </Typography>
                                          <Typography>
                                            {item.doctor_count}
                                          </Typography>
                                        </Grid>

                                        <Grid
                                          item
                                          xs={3}
                                          sm={6}
                                          md={1}
                                          style={{ textAlign: "center" }}
                                        >
                                          <Typography variant="subtitle2">
                                            Nurses
                                          </Typography>
                                          <Typography>
                                            {item.nurse_count}
                                          </Typography>
                                        </Grid>

                                        <Grid
                                          item
                                          xs={12}
                                          sm={6}
                                          md={3}
                                          style={{ textAlign: "center" }}
                                        >
                                          <Tooltip title="Edit Hospital">
                                            <IconButton
                                              data-test="hospital-edit-btn"
                                              aria-label="edit"
                                              size="large"
                                              color="primary"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                handleAddEditHospital(item.id);
                                              }}
                                            >
                                              <EditIcon fontSize="inherit" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Delete Hospital">
                                            <IconButton
                                              data-test="hospital-delete-btn"
                                              aria-label="delete"
                                              size="large"
                                              color="red"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                showSnackBar(
                                                  "info",
                                                  "Info",
                                                  "This feature is not available."
                                                );
                                              }}
                                            >
                                              <DeleteIcon fontSize="inherit" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Help">
                                            <IconButton
                                              data-test="hospital-help-btn"
                                              aria-label="help"
                                              size="large"
                                              color="primary"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                showSnackBar(
                                                  "info",
                                                  "Info",
                                                  "This feature is not available."
                                                );
                                              }}
                                            >
                                              <HelpIcon fontSize="inherit" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Daily Report">
                                            <IconButton
                                              data-test="hospital-daily-report-btn"
                                              aria-label="daily-report"
                                              size="large"
                                              color="primary"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                history.push({
                                                  pathname: "/reports",
                                                  search: `?type=${encodeURIComponent(
                                                    "Daily"
                                                  )}&hospitalID=${encodeURIComponent(
                                                    item.id
                                                  )}&wardID=${encodeURIComponent(
                                                    null
                                                  )}`,
                                                });
                                              }}
                                            >
                                              <AssessmentIcon fontSize="inherit" />
                                            </IconButton>
                                          </Tooltip>
                                        </Grid>
                                      </Grid>
                                    </CardActionArea>
                                  </Card>
                                </ListItem>
                              ))}
                          </List>
                        </Grid>
                      ) : (
                        <Grid item xs={12}>
                          <Typography style={{ textAlign: "center" }}>
                            No records to display
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Typography
                  data-test={"hospital-empty-msg"}
                  style={{ textAlign: "center" }}
                >
                  No records to display
                </Typography>
              </Grid>
            )}

            {isHospitalAddEditVisible && (
              <HospitalAddEditDialog
                isVisible={isHospitalAddEditVisible}
                onClose={handelCloseHospital}
                onSave={handelSaveHospital}
                hospitalID={selectedHospitalID}
              />
            )}
          </>
        )}
      </Grid>
    </Box>
  );
}
